// Vendor types: what an open call on /vendors is looking for (face painting,
// food trucks, makers...). Data, not a constant: the vocabulary is
// lyh_vendor_types and each call's picks are rows in lyh_event_vendor_types.
// See supabase/migrations/20260926023000_vendor_types.sql for why a join table
// rather than a column on lyh_events.

import { useEffect, useState } from 'react'
import { supabase } from './supabase'

export interface VendorType {
  slug: string
  label: string
}

export interface VendorTypesData {
  /** The vocabulary, in display order. */
  types: VendorType[]
  /** event id -> the slugs that call is looking for, in vocabulary order. */
  byEvent: Map<string, string[]>
  /** event id -> its application deadline, 'YYYY-MM-DD' (lyh_event_vendor_deadlines). */
  deadlines: Map<string, string>
  loaded: boolean
}

type Link = { event_id: string; vendor_type: string }
type Deadline = { event_id: string; deadline: string }

function build(
  types: VendorType[],
  links: readonly Link[],
  deadlineRows: readonly Deadline[],
): VendorTypesData {
  const order = new Map(types.map((t, i) => [t.slug, i]))
  const byEvent = new Map<string, string[]>()
  for (const r of links) {
    const list = byEvent.get(r.event_id) ?? []
    list.push(r.vendor_type)
    byEvent.set(r.event_id, list)
  }
  for (const list of byEvent.values()) {
    list.sort((a, b) => (order.get(a) ?? 99) - (order.get(b) ?? 99))
  }
  const deadlines = new Map(deadlineRows.map((d) => [d.event_id, d.deadline]))
  return { types, byEvent, deadlines, loaded: true }
}

// One copy for the whole app, so the snapshot can fill it before /vendors has
// even mounted, and every component reading it sees the same data.
let current: VendorTypesData = { types: [], byEvent: new Map(), deadlines: new Map(), loaded: false }
const listeners = new Set<(d: VendorTypesData) => void>()
function publish(d: VendorTypesData) {
  current = d
  for (const l of listeners) l(d)
}

/**
 * Filled from /events.json, the CDN snapshot the calendar boots from
 * (lib/useEvents, netlify/functions/events-json.mts), so /vendors paints its
 * chips and filter in the same pass as its calls, the way the calendar paints
 * its week, instead of waiting on two Supabase calls of its own.
 */
export function primeVendorTypes(
  types: VendorType[],
  links: readonly Link[],
  deadlines: readonly Deadline[],
): void {
  if (livePublished) return // the live pass is newer; never step back to the snapshot
  publish(build(types, links, deadlines))
}

let livePublished = false
let liveStarted = false

/**
 * The vocabulary and each call's types. Starts from whatever the snapshot
 * already primed, then Supabase supersedes it once per session, the same
 * snapshot-then-live order lib/useEvents uses for the events themselves. A
 * failed live fetch keeps the snapshot copy; with neither, the page shows its
 * calls with no chips and no filter row, which is the page as it was before
 * types existed.
 */
export function useVendorTypes(): VendorTypesData {
  const [data, setData] = useState<VendorTypesData>(current)

  useEffect(() => {
    listeners.add(setData)
    setData(current)
    if (!liveStarted) {
      liveStarted = true
      void Promise.all([
        supabase.from('lyh_vendor_types').select('slug,label').order('sort_order'),
        supabase.from('lyh_event_vendor_types').select('event_id,vendor_type'),
        supabase.from('lyh_event_vendor_deadlines').select('event_id,deadline'),
      ]).then(([typesRes, linksRes, deadlinesRes]) => {
        if (typesRes.error || linksRes.error || deadlinesRes.error) {
          console.error('load vendor types', typesRes.error ?? linksRes.error ?? deadlinesRes.error)
          if (!current.loaded) publish({ ...current, loaded: true })
          return
        }
        livePublished = true
        publish(
          build(
            typesRes.data as VendorType[],
            linksRes.data as Link[],
            deadlinesRes.data as Deadline[],
          ),
        )
      })
    }
    return () => {
      listeners.delete(setData)
    }
  }, [])

  return data
}

/**
 * Map what a host picked on the Post a call form to vocabulary slugs. The form
 * stores labels ("Food trucks"); older submissions may carry a label that has
 * since been renamed, and "Other" is deliberately not a type, so anything
 * unmatched is dropped rather than guessed.
 */
export function labelsToSlugs(labels: readonly string[], types: readonly VendorType[]): string[] {
  const byLabel = new Map(types.map((t) => [t.label.toLowerCase(), t.slug]))
  const slugs = new Set(types.map((t) => t.slug))
  const out: string[] = []
  for (const l of labels) {
    const key = l.trim().toLowerCase()
    const slug = byLabel.get(key) ?? (slugs.has(key) ? key : undefined)
    if (slug && !out.includes(slug)) out.push(slug)
  }
  return out
}
